import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { updateMySettings, type Lang } from '@/api';
import { Skeleton } from '@/components/Skeleton';
import { useCloudSynced } from '@/hooks';
import { launchLanguage } from '@/platform';
import { hasStrings, loadStrings } from '@/str';

const KEY = 'lang';
const STRINGS_WAIT_MS = 3000;

export function langFromCode(code: string | undefined): Lang {
  return code?.startsWith('ru') ? 'ru' : 'uzl';
}

const DEFAULT: Lang = langFromCode(launchLanguage());

export const LANGS: { value: Lang; label: string }[] = [
  { value: 'uzl', label: 'O‘zbekcha' },
  { value: 'uzk', label: 'Ўзбекча' },
  { value: 'ru', label: 'Русский' },
];

const asLang = (value: unknown): Lang | null => LANGS.find((l) => l.value === value)?.value ?? null;

interface LangState {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LangContext = createContext<LangState>({ lang: DEFAULT, setLang: () => {} });

export function useLang(): LangState {
  return useContext(LangContext);
}

function storedLang(): Lang {
  try {
    return asLang(localStorage.getItem(KEY)) ?? DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export function preloadStrings(): void {
  void loadStrings(storedLang()).catch(() => {});
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, store] = useCloudSynced<Lang>(KEY, asLang, DEFAULT);
  const [failed, setFailed] = useState<Lang | null>(null);
  const [, arrived] = useState(0);
  const ready = hasStrings(lang) || failed === lang;
  const [shown, setShown] = useState<Lang | null>(ready ? lang : null);
  if (ready && shown !== lang) setShown(lang);

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    const slow =
      shown === null
        ? window.setTimeout(() => {
            if (!cancelled) setFailed(lang);
          }, STRINGS_WAIT_MS)
        : 0;
    loadStrings(lang).then(
      () => {
        window.clearTimeout(slow);
        if (!cancelled) arrived((n) => n + 1);
      },
      () => {
        window.clearTimeout(slow);
        if (!cancelled) setFailed(lang);
      },
    );
    return () => {
      cancelled = true;
      window.clearTimeout(slow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const setLang = (next: Lang) => {
    if (next === lang) return;
    store(next);
    void updateMySettings({ language: next }).catch(() => {});
  };

  if (shown === null) return <Skeleton shape="rows" count={7} />;

  return <LangContext.Provider value={{ lang: shown, setLang }}>{children}</LangContext.Provider>;
}
